import { useCallback, useEffect, useMemo, useState } from "react";
import {
  createPublicClient,
  createWalletClient,
  custom,
  formatEther,
  http,
  parseEther,
  parseUnits,
  type Address,
  type EIP1193Provider,
  type Hex,
} from "viem";
import { demoLaunches, FACTORY_ADDRESS, factoryAbi, gatewayAbi, hyperEvm, quoterV1Abi, quoterV2Abi, tokenAbi, type Launch } from "./contracts";

declare global {
  interface Window { ethereum?: EIP1193Provider }
}

export const publicClient = createPublicClient({ chain: hyperEvm, transport: http() });

export function resolveUri(uri?: string) {
  if (!uri) return "";
  return uri.startsWith("ipfs://") ? `https://ipfs.io/ipfs/${uri.slice(7)}` : uri;
}

async function metadataFor(uri: string) {
  if (!uri) return {};
  try {
    const response = await fetch(resolveUri(uri), { signal: AbortSignal.timeout(4500) });
    if (!response.ok) return {};
    return await response.json() as { image?: string; description?: string; external_url?: string };
  } catch { return {}; }
}

export function useDeaux() {
  const [account, setAccount] = useState<Address>();
  const [launches, setLaunches] = useState<Launch[]>(FACTORY_ADDRESS ? [] : demoLaunches);
  const [gateway, setGateway] = useState<Address>();
  const [loading, setLoading] = useState(Boolean(FACTORY_ADDRESS));
  const [error, setError] = useState("");
  const [pending, setPending] = useState("");

  const walletClient = useMemo(() => window.ethereum
    ? createWalletClient({ account, chain: hyperEvm, transport: custom(window.ethereum) })
    : undefined, [account]);

  const ensureNetwork = useCallback(async () => {
    if (!window.ethereum) throw new Error("Install an EVM wallet such as Rabby or MetaMask.");
    try {
      await window.ethereum.request({ method: "wallet_switchEthereumChain", params: [{ chainId: "0x3e7" }] });
    } catch (switchError) {
      const code = (switchError as { code?: number }).code;
      if (code !== 4902) throw switchError;
      await window.ethereum.request({ method: "wallet_addEthereumChain", params: [{
        chainId: "0x3e7", chainName: "HyperEVM",
        nativeCurrency: { name: "HYPE", symbol: "HYPE", decimals: 18 },
        rpcUrls: [hyperEvm.rpcUrls.default.http[0]],
        blockExplorerUrls: [hyperEvm.blockExplorers?.default.url],
      }] });
    }
  }, []);

  const connect = useCallback(async () => {
    setError("");
    try {
      await ensureNetwork();
      const accounts = await window.ethereum!.request({ method: "eth_requestAccounts" }) as Address[];
      setAccount(accounts[0]);
    } catch (reason) { setError(readableError(reason)); }
  }, [ensureNetwork]);

  const loadLaunches = useCallback(async () => {
    const factory = FACTORY_ADDRESS;
    if (!factory) return;
    setLoading(true);
    try {
      const [count, gatewayAddress] = await Promise.all([
        publicClient.readContract({ address: factory, abi: factoryAbi, functionName: "launchCount" }),
        publicClient.readContract({ address: factory, abi: factoryAbi, functionName: "swapGateway" }),
      ]);
      setGateway(gatewayAddress);
      const start = count > 60n ? count - 60n : 0n;
      const rows = await Promise.all(Array.from({ length: Number(count - start) }, async (_, offset) => {
        const index = count - 1n - BigInt(offset);
        const [token, pool, creator, createdAt, metadataURI] = await publicClient.readContract({ address: factory, abi: factoryAbi, functionName: "launchAt", args: [index] });
        const [name, symbol, bonded, bondingTarget, bondingBalance, metadata] = await Promise.all([
          publicClient.readContract({ address: token, abi: tokenAbi, functionName: "name" }),
          publicClient.readContract({ address: token, abi: tokenAbi, functionName: "symbol" }),
          publicClient.readContract({ address: token, abi: tokenAbi, functionName: "bonded" }),
          publicClient.readContract({ address: token, abi: tokenAbi, functionName: "bondingTarget" }),
          publicClient.readContract({ address: gatewayAddress, abi: gatewayAbi, functionName: "bondingBalance", args: [token] }),
          metadataFor(metadataURI),
        ]);
        return { token, pool, creator, createdAt: Number(createdAt), metadataURI, name, symbol, bonded, bondingTarget, bondingBalance, image: resolveUri(metadata.image), description: metadata.description, website: metadata.external_url } satisfies Launch;
      }));
      setLaunches(rows);
    } catch (reason) { setError(`Could not read the launchpad: ${readableError(reason)}`); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { void loadLaunches(); }, [loadLaunches]);
  useEffect(() => {
    if (!window.ethereum) return;
    const onAccounts = (accounts: unknown) => setAccount((accounts as Address[])[0]);
    window.ethereum.request({ method: "eth_accounts" }).then(onAccounts).catch(() => undefined);
    window.ethereum.on?.("accountsChanged", onAccounts);
    return () => window.ethereum?.removeListener?.("accountsChanged", onAccounts);
  }, []);

  const transact = useCallback(async (label: string, fn: () => Promise<Hex>) => {
    if (!account || !walletClient) { await connect(); throw new Error("Connect your wallet to continue."); }
    setError(""); setPending(label);
    try {
      await ensureNetwork();
      const hash = await fn();
      setPending("Confirming on HyperEVM…");
      await publicClient.waitForTransactionReceipt({ hash });
      await loadLaunches();
      return hash;
    } catch (reason) { const message = readableError(reason); setError(message); throw new Error(message); }
    finally { setPending(""); }
  }, [account, walletClient, connect, ensureNetwork, loadLaunches]);

  const createToken = useCallback(async (values: { name: string; symbol: string; description: string; website: string; initialBuy: string; image?: File }) => {
    const factory = FACTORY_ADDRESS;
    if (!factory || !account || !walletClient) throw new Error("Connect a deployed factory and wallet first.");
    await ensureNetwork();
    const usingBigBlocks = await hyperRpc<boolean>("eth_usingBigBlocks", [account]);
    if (!usingBigBlocks) throw new Error("Big blocks are not enabled for this wallet. Enable HyperEVM big blocks, wait for confirmation, then try again.");
    let image = "";
    if (values.image) {
      const body = new FormData(); body.append("file", values.image);
      const response = await fetch("/api/upload", { method: "POST", body });
      const upload = await response.json() as { url?: string; error?: string };
      if (!response.ok || !upload.url) throw new Error(upload.error || "Image upload failed.");
      image = upload.url;
    }
    const metaResponse = await fetch("/api/metadata", { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ name: values.name, symbol: values.symbol, description: values.description, image, website: values.website }) });
    if (!metaResponse.ok) throw new Error("Metadata upload failed.");
    const metadataURI = (await metaResponse.json() as { url: string }).url;
    const initial = parseEther(values.initialBuy || "0");
    const fee = await publicClient.readContract({ address: factory, abi: factoryAbi, functionName: "LAUNCH_FEE" });
    const value = fee + initial;
    const args = [values.name, values.symbol.toUpperCase(), metadataURI, 0n, deadline()] as const;
    const estimatedGas = await publicClient.estimateContractGas({ account, address: factory, abi: factoryAbi, functionName: "createToken", args, value });
    const gas = estimatedGas * 120n / 100n;
    const bigBlockGasPrice = BigInt(await hyperRpc<Hex>("eth_bigBlockGasPrice"));
    const liveBigBlockPrice = bigBlockGasPrice * 2n;
    const walletSimulationFloor = parseUnits("2", 9);
    const gasPrice = liveBigBlockPrice > walletSimulationFloor ? liveBigBlockPrice : walletSimulationFloor;
    const balance = await publicClient.getBalance({ address: account });
    const required = value + gas * gasPrice;
    if (balance < required) throw new Error(`Insufficient HYPE. Keep at least ${Number(formatEther(required)).toFixed(4)} HYPE available for the launch and big-block gas.`);

    return transact("Approve launch", () => walletClient.writeContract({ account, chain: hyperEvm, address: factory, abi: factoryAbi, functionName: "createToken", args, value, gas, gasPrice }));
  }, [account, walletClient, transact, ensureNetwork]);

  const buy = useCallback(async (token: Address, amount: string) => {
    if (!gateway || !account || !walletClient) throw new Error("Connect your wallet first.");
    const amountIn = parseEther(amount);
    const minimumOut = await quoteMinimum(token, gateway, amountIn, "buy");
    return transact(`Buying with ${amount} HYPE`, () => walletClient.writeContract({ account, chain: hyperEvm, address: gateway, abi: gatewayAbi, functionName: "buy", args: [token, minimumOut, account, deadline()], value: amountIn }));
  }, [gateway, account, walletClient, transact]);

  const sell = useCallback(async (token: Address, amount: string) => {
    if (!gateway || !account || !walletClient) throw new Error("Connect your wallet first.");
    const tokens = parseUnits(amount, 18);
    const minimumOut = await quoteMinimum(token, gateway, tokens, "sell");
    const allowance = await publicClient.readContract({ address: token, abi: tokenAbi, functionName: "allowance", args: [account, gateway] });
    if (allowance < tokens) await transact("Approve token", () => walletClient.writeContract({ account, chain: hyperEvm, address: token, abi: tokenAbi, functionName: "approve", args: [gateway, tokens] }));
    return transact(`Selling ${amount} tokens`, () => walletClient.writeContract({ account, chain: hyperEvm, address: gateway, abi: gatewayAbi, functionName: "sell", args: [token, tokens, minimumOut, account, deadline()] }));
  }, [gateway, account, walletClient, transact]);

  const balances = useCallback(async (token: Address) => {
    if (!account) return { hype: 0n, token: 0n };
    const [hype, tokenBalance] = await Promise.all([
      publicClient.getBalance({ address: account }),
      publicClient.readContract({ address: token, abi: tokenAbi, functionName: "balanceOf", args: [account] }),
    ]);
    return { hype, token: tokenBalance };
  }, [account]);

  const quote = useCallback(async (token: Address, amount: string, side: "buy" | "sell") => {
    if (!gateway || !amount || Number(amount) <= 0) return 0n;
    return quoteAmount(token, gateway, parseUnits(amount, 18), side);
  }, [gateway]);

  return { account, launches, loading, error, pending, connect, createToken, buy, sell, balances, quote, refresh: loadLaunches, isLive: Boolean(FACTORY_ADDRESS), formatHype: (value: bigint) => Number(formatEther(value)).toFixed(1) };
}

function deadline() { return BigInt(Math.floor(Date.now() / 1000) + 20 * 60); }
async function hyperRpc<T>(method: string, params: unknown[] = []) {
  const response = await fetch(hyperEvm.rpcUrls.default.http[0], {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ jsonrpc: "2.0", id: Date.now(), method, params }),
  });
  const payload = await response.json() as { result?: T; error?: { message?: string } };
  if (!response.ok || payload.error || payload.result === undefined) throw new Error(payload.error?.message || `HyperEVM ${method} failed.`);
  return payload.result;
}
async function quoteMinimum(token: Address, gateway: Address, amountIn: bigint, side: "buy" | "sell") {
  return (await quoteAmount(token, gateway, amountIn, side)) * 9_800n / 10_000n;
}
async function quoteAmount(token: Address, gateway: Address, amountIn: bigint, side: "buy" | "sell") {
  const [quoter, wrappedNative] = await Promise.all([
    publicClient.readContract({ address: token, abi: tokenAbi, functionName: "quoter" }),
    publicClient.readContract({ address: gateway, abi: gatewayAbi, functionName: "wrappedNative" }),
  ]);
  const tokenIn = side === "buy" ? wrappedNative : token;
  const tokenOut = side === "buy" ? token : wrappedNative;
  let amountOut: bigint;
  try {
    const result = await publicClient.simulateContract({ address: quoter, abi: quoterV2Abi, functionName: "quoteExactInputSingle", args: [{ tokenIn, tokenOut, amountIn, fee: 10_000, sqrtPriceLimitX96: 0n }] });
    amountOut = result.result[0];
  } catch {
    const result = await publicClient.simulateContract({ address: quoter, abi: quoterV1Abi, functionName: "quoteExactInputSingle", args: [tokenIn, tokenOut, 10_000, amountIn, 0n] });
    amountOut = result.result;
  }
  return amountOut;
}
function readableError(reason: unknown) {
  const error = reason as { shortMessage?: string; message?: string };
  const message = error.shortMessage || error.message || "Something went wrong.";
  return message.replace(/Details:.*/s, "").replace(/Contract Call:.*/s, "").trim();
}
